import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { Chip, Icon, SimpleModal, TableLoader, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { dateToFormat } from 'src/utils/date';
import useStyles from './allowedSendersModalStyles';
const MATCH_TYPES = {
  exact: 'ExactAddress',
  domain: 'Domain'
};

/** What each kind means, said where the choice is made rather than in a tooltip nobody opens. */
const MATCH_TYPE_COPY = {
  [MATCH_TYPES.exact]: 'Trusts this one address only.',
  [MATCH_TYPES.domain]: 'Trusts every address on the domain — utilities send from changing addresses (billing@, no-reply-7@, …), so one domain entry covers them all.'
};

/**
 * Add one sender. Its own module-level component (never inline in the modal render) so typing does
 * not remount the fields. The matched value is only editable while adding: the service treats a
 * row's value as its identity, so a saved row offers rename and enable/disable, and a typo is fixed
 * by remove + add.
 */
const SenderForm = _ref => {
  let editing = _ref.editing,
    isSaving = _ref.isSaving,
    onSubmit = _ref.onSubmit,
    onCancel = _ref.onCancel;
  const classes = useStyles();
  const isNew = editing === 'new';
  const _useState = useState({
      matchType: isNew ? MATCH_TYPES.exact : editing.matchType,
      matchValue: isNew ? '' : editing.matchValue,
      displayName: (isNew ? '' : editing.displayName) || ''
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const handleSubmit = event => {
    event.preventDefault();
    onSubmit({
      matchType: state.matchType,
      matchValue: state.matchValue.trim(),
      displayName: state.displayName.trim()
    });
  };
  const canSave = (isNew ? state.matchValue.trim().length > 0 : true) && !isSaving;
  const isDomain = state.matchType === MATCH_TYPES.domain;
  return /*#__PURE__*/React.createElement("form", {
    className: classes.form,
    onSubmit: handleSubmit
  }, isNew && /*#__PURE__*/React.createElement(RadioGroup, {
    row: true,
    value: state.matchType,
    onChange: event => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      matchType: event.target.value
    }))
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: MATCH_TYPES.exact,
    control: /*#__PURE__*/React.createElement(Radio, {
      disabled: isSaving
    }),
    label: "Exact address"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: MATCH_TYPES.domain,
    control: /*#__PURE__*/React.createElement(Radio, {
      disabled: isSaving
    }),
    label: "Whole domain"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.formRow
  }, /*#__PURE__*/React.createElement(TextField, {
    className: classes.field,
    label: isDomain ? 'Domain' : 'Email address',
    placeholder: isDomain ? 'conedison.com' : 'billing@conedison.com',
    value: state.matchValue,
    onChange: event => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      matchValue: event.target.value
    })),
    helperText: isNew ? MATCH_TYPE_COPY[state.matchType] : 'To change what this entry matches, remove it and add the corrected one.',
    disabled: !isNew || isSaving,
    autoFocus: isNew,
    required: isNew
  }), /*#__PURE__*/React.createElement(TextField, {
    className: classes.field,
    label: "Label (optional)",
    placeholder: "Con Edison billing",
    value: state.displayName,
    onChange: event => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      displayName: event.target.value
    })),
    helperText: "A name for the list; the matching only ever uses the address or domain.",
    disabled: isSaving,
    autoFocus: !isNew
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.formRow
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    type: "submit",
    disabled: !canSave
  }, isSaving ? 'Saving…' : 'Save'), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    onClick: onCancel,
    disabled: isSaving
  }, "Cancel")));
};

/**
 * The senders this company's bill-intake address accepts mail from (LB-5781, over the LB-5764 API).
 *
 * This is the undo half of sender trust: approving happens here and (soon) from a rejected bill
 * itself; seeing who is trusted, disabling one, and removing one happen only here. Disabled rows
 * stay listed — a sender that was deliberately turned off is information, not clutter.
 */
export const AllowedSendersModal = observer(_ref2 => {
  let trigger = _ref2.trigger,
    isOpen = _ref2.isOpen,
    handleOpen = _ref2.handleOpen,
    handleClose = _ref2.handleClose;
  const store = useStore();
  const classes = useStyles();
  const _useState3 = useState({
      senders: [],
      isLoading: true,
      // Opposite meanings: "none yet" invites adding one, "could not load" must not.
      loadFailed: false,
      isSaving: false,
      editing: null,
      // null | 'new' | the row being renamed
      pendingDelete: null,
      // Recently-rejected senders offered for one-click approval (LB-5811).
      suggestions: [],
      selected: {},
      // senderEmail -> boolean
      approveAs: MATCH_TYPES.exact,
      // approve ticked senders as exact addresses or their whole domains
      isApproving: false
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    state = _useState4[0],
    setState = _useState4[1];
  const loadSenders = async () => {
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isLoading: true,
      editing: null,
      pendingDelete: null
    }));

    // The list is required; suggestions are a bonus. Fetch together (two separate async models, so
    // they don't cancel each other) and let a suggestions failure — e.g. an older backend without
    // the endpoint — degrade to "no suggestions" rather than failing the whole modal. The
    // suggestions action is silent (createSilentAsync) and its run() resolves undefined on failure
    // rather than rejecting, so the fallback is the undefined check below, not a catch.
    const _await$Promise$all = await Promise.all([store.allowedSenders.getAllowedSendersAsync.run(), store.allowedSenders.getAllowedSenderSuggestionsAsync.run()]),
      _await$Promise$all2 = _slicedToArray(_await$Promise$all, 2),
      listResp = _await$Promise$all2[0],
      suggestResp = _await$Promise$all2[1];
    const failed = !listResp || listResp.isSuccessful === false;
    const suggestions = suggestResp && suggestResp.isSuccessful !== false ? suggestResp.response || [] : [];
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      senders: failed ? [] : listResp.response || [],
      loadFailed: failed,
      isLoading: false,
      suggestions,
      selected: {}
    }));
  };
  const selectedEmails = () => state.suggestions.filter(s => state.selected[s.senderEmail]).map(s => s.senderEmail);
  const handleApproveSelected = async () => {
    const chosen = state.suggestions.filter(s => state.selected[s.senderEmail]);
    if (chosen.length === 0) return;
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isApproving: true
    }));

    // Approve as exact addresses, or as their (deduplicated) domains — one choice for the whole
    // selection. Each is the existing add call; the async layer toasts any the server rejects.
    const asDomain = state.approveAs === MATCH_TYPES.domain;
    const values = asDomain ? [...new Set(chosen.map(s => s.domain).filter(Boolean))].map(domain => ({
      matchType: MATCH_TYPES.domain,
      matchValue: domain,
      displayName: ''
    })) : chosen.map(s => ({
      matchType: MATCH_TYPES.exact,
      matchValue: s.senderEmail,
      displayName: ''
    }));

    // Sequential, not Promise.all: addAllowedSenderAsync is one async-model instance whose run()
    // cancels its own previous in-flight request (single-flight by design), so firing the whole
    // selection at once would cancel every add but the last and approve just one. run() resolves
    // undefined on failure instead of rejecting — the async layer already toasts the server's
    // message — so a plain await per value is enough and an uncounted result means it did not save.
    let ok = 0;
    for (const value of values) {
      const resp = await store.allowedSenders.addAllowedSenderAsync.run(value);
      if (resp && resp.isSuccessful !== false) ok += 1;
    }
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isApproving: false
    }));
    // Count the unit actually added: domains when approving by domain (one domain can cover several
    // of the ticked senders), individual addresses otherwise — so the message never says "1 sender"
    // for what was really one domain.
    const unit = asDomain ? 'domain' : 'sender';
    if (ok > 0) toast('success', {
      body: ["Approved ".concat(ok, " ").concat(unit).concat(ok === 1 ? '' : 's')]
    });
    await loadSenders();
  };
  const toggleSuggestion = email => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
    selected: _objectSpread(_objectSpread({}, previous.selected), {}, {
      [email]: !previous.selected[email]
    })
  }));
  const allSuggestionsSelected = state.suggestions.length > 0 && state.suggestions.every(s => state.selected[s.senderEmail]);
  const toggleSelectAll = () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
    selected: allSuggestionsSelected ? {} : Object.fromEntries(previous.suggestions.map(s => [s.senderEmail, true]))
  }));
  useEffect(() => {
    if (!isOpen) return;
    loadSenders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const handleSave = async values => {
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isSaving: true
    }));
    const editingRow = state.editing !== 'new' ? state.editing : null;
    const resp = editingRow ? await store.allowedSenders.updateAllowedSenderAsync.run({
      senderId: editingRow.id,
      displayName: values.displayName
    }) : await store.allowedSenders.addAllowedSenderAsync.run(values);
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      isSaving: false
    }));

    // The async layer already toasts the server's message on failure — for a duplicate that message
    // says whether the existing entry is just disabled, which is the actionable part.
    if (!resp || resp.isSuccessful === false) return;
    toast('success', {
      body: [editingRow ? 'Sender updated' : 'Sender allowed']
    });
    await loadSenders();
  };
  const handleToggleActive = async sender => {
    const resp = await store.allowedSenders.updateAllowedSenderAsync.run({
      senderId: sender.id,
      isActive: !sender.isActive
    });
    if (!resp || resp.isSuccessful === false) return;
    toast('success', {
      body: [sender.isActive ? 'Sender disabled — its mail is rejected again' : 'Sender enabled']
    });
    await loadSenders();
  };
  const handleDeleteConfirm = async _ref3 => {
    let action = _ref3.action;
    const row = state.pendingDelete;
    setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      pendingDelete: null
    }));
    if (action !== 'submit' || !row) return;
    const resp = await store.allowedSenders.deleteAllowedSenderAsync.run({
      senderId: row.id
    });
    if (!resp || resp.isSuccessful === false) return;
    toast('success', {
      body: ['Sender removed']
    });
    await loadSenders();
  };
  const renderRow = sender => /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.row, " ").concat(sender.isActive ? '' : classes.rowDisabled),
    key: sender.id
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cell,
    style: {
      flex: 2
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.value
  }, sender.matchType === MATCH_TYPES.domain ? "@".concat(sender.matchValue) : sender.matchValue), !!sender.displayName && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.label
  }, sender.displayName)), /*#__PURE__*/React.createElement("div", {
    className: classes.cell
  }, sender.matchType === MATCH_TYPES.domain ? /*#__PURE__*/React.createElement(Chip, {
    label: "Whole domain",
    variant: "info"
  }) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.typeLabel
  }, "Exact address")), /*#__PURE__*/React.createElement("div", {
    className: classes.cell
  }, !sender.isActive && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.disabledLabel
  }, "Disabled")), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "Rename sender",
    title: "Rename",
    onClick: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      editing: sender
    }))
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "edit"
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    onClick: () => handleToggleActive(sender)
  }, sender.isActive ? 'Disable' : 'Enable'), /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "Remove sender",
    title: "Remove",
    onClick: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
      pendingDelete: sender
    }))
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))));
  const renderSuggestions = () => {
    if (state.suggestions.length === 0) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.suggestions
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.suggestionsHead
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.suggestionsTitle
    }, "Recently rejected \u2014 approve the ones you recognize"), /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: toggleSelectAll,
      disabled: state.isApproving
    }, allSuggestionsSelected ? 'Clear all' : 'Select all')), state.suggestions.map(s => /*#__PURE__*/React.createElement(FormControlLabel, {
      key: s.senderEmail,
      className: classes.suggestionRow,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: !!state.selected[s.senderEmail],
        onChange: () => toggleSuggestion(s.senderEmail),
        disabled: state.isApproving
      }),
      label: /*#__PURE__*/React.createElement("span", {
        className: classes.suggestionText
      }, /*#__PURE__*/React.createElement("span", {
        className: classes.value
      }, s.senderEmail), /*#__PURE__*/React.createElement("span", {
        className: classes.suggestionMeta
      }, "seen ".concat(s.count, " time").concat(s.count === 1 ? '' : 's', ", last ").concat(dateToFormat(s.lastReceivedAt))))
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.suggestionsActions
    }, /*#__PURE__*/React.createElement(RadioGroup, {
      row: true,
      value: state.approveAs,
      onChange: event => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
        approveAs: event.target.value
      }))
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      value: MATCH_TYPES.exact,
      control: /*#__PURE__*/React.createElement(Radio, {
        disabled: state.isApproving
      }),
      label: "these addresses"
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      value: MATCH_TYPES.domain,
      control: /*#__PURE__*/React.createElement(Radio, {
        disabled: state.isApproving
      }),
      label: "their whole domains"
    })), /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleApproveSelected,
      disabled: state.isApproving || selectedEmails().length === 0
    }, state.isApproving ? 'Approving…' : "Approve selected (".concat(selectedEmails().length, ")"))));
  };
  const renderBody = () => {
    if (state.isLoading) return /*#__PURE__*/React.createElement(TableLoader, null);
    if (state.loadFailed) {
      return /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        className: classes.empty
      }, "The allowed senders could not be loaded. Close and try again.");
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.trustNote
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "info"
    }), /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, "@livable.com and your confirmed team members are already trusted \u2014 only add outside senders (e.g. a utility company) here.")), renderSuggestions(), state.senders.length === 0 && state.editing === null && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      className: classes.empty
    }, "No senders are allowed yet. Bills emailed from an unknown sender are rejected until the sender \u2014 or their whole domain \u2014 is added here."), state.senders.map(renderRow), state.editing !== null ? /*#__PURE__*/React.createElement(SenderForm, {
      key: state.editing === 'new' ? 'new' : state.editing.id,
      editing: state.editing,
      isSaving: state.isSaving,
      onSubmit: handleSave,
      onCancel: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
        editing: null
      }))
    }) : /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      onClick: () => setState(previous => _objectSpread(_objectSpread({}, previous), {}, {
        editing: 'new'
      }))
    }, "Add a sender manually"));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: trigger,
    isOpen: isOpen,
    handleOpen: handleOpen,
    handleClose: handleClose,
    title: "Allowed senders",
    subTitle: "Email from these senders is accepted at your bill-intake address. Anyone else is rejected."
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, renderBody())), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    externalIsOpen: !!state.pendingDelete,
    headerText: "Remove this sender?",
    bodyText: state.pendingDelete ? "Bills emailed from ".concat(state.pendingDelete.matchType === MATCH_TYPES.domain ? "@".concat(state.pendingDelete.matchValue) : state.pendingDelete.matchValue, " will be rejected. If they might come back, Disable keeps the entry.") : '',
    customSubmitButtonName: "Remove",
    callBack: handleDeleteConfirm
  }));
});